import { DiscordIcon } from '../../components/icons/DiscordIcon';
import { FacebookIcon } from '../../components/icons/FacebookIcon';
import { InstagramIcon } from '../../components/icons/InstagramIcon';
import { TikTokIcon } from '../../components/icons/TikTokIcon';
import { XIcon } from '../../components/icons/XIcon';
import { trackCtaClick } from '../../utils/ctaTracking';
import type { FooterSectionProps } from '../types';

export function FooterSection({ t, tracking, openLegal }: FooterSectionProps) {
  return (
    <footer className="relative min-h-[690px] overflow-hidden pb-8 pt-4 text-white sm:min-h-[700px] lg:min-h-[620px]">
      <div className="absolute inset-0 bg-[#020816]" aria-hidden="true" />

      <div className="pointer-events-none absolute inset-0 overflow-hidden select-none" aria-hidden="true">
        <div
          className="absolute inset-0 bg-no-repeat sm:hidden"
          style={{
            backgroundImage: 'url(/footer/footer-mobileV2.png)',
            backgroundPosition: 'center bottom',
            backgroundSize: '100% auto',
          }}
        />
        <div
          className="absolute inset-0 hidden bg-no-repeat sm:block lg:hidden"
          style={{
            backgroundImage: 'url(/footer/footer-tablet.png)',
            backgroundPosition: 'center bottom',
            backgroundSize: 'cover',
          }}
        />
        <div
          className="absolute inset-0 hidden bg-no-repeat lg:block"
          style={{
            backgroundImage: 'url(/footer/footer-desktop.png)',
            backgroundPosition: 'center bottom',
            backgroundSize: 'cover',
          }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl px-2 pb-8 pt-[86px] sm:px-8 sm:pt-[90px] lg:pt-[84px]">
        <div className="grid w-full grid-cols-2 items-start gap-x-3 gap-y-8 sm:gap-x-10 sm:gap-y-10 lg:grid-cols-[1.2fr_0.55fr_0.9fr_1fr]">
          <div className="col-span-2 max-w-[520px] space-y-6 lg:col-span-1 lg:col-start-1">
            <h2 className="font-pangaia text-[32px] font-bold leading-[38px] sm:text-[40px] sm:leading-[48px]">
              {t.footer_heading || 'Thousands of untold stories await you'}
            </h2>
            <a
              href={tracking.registerUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackCtaClick({
                  placement: 'footer_join',
                  language: tracking.language,
                  destination: tracking.registerUrl,
                  hasAnalyticsConsent: tracking.hasAnalyticsConsent,
                })
              }
              className="landing-cta-interactive inline-flex items-center gap-3 rounded-full bg-white px-6 py-2 font-pangram text-[14px] text-[#1C1C1E] sm:text-[16px]"
            >
              {t.footer_join || 'Join Lucid Book'}
              <svg className="landing-cta-dot" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <circle cx="6" cy="6" r="6" fill="#6195FF" />
              </svg>
            </a>
          </div>

          <div className="col-span-1 lg:col-start-3 lg:justify-self-start">
            <h3 className="mb-4 font-pangram text-[14px] font-semibold leading-[20px] tracking-[0.02em]">
              {t.footer_legal || 'Legal'}
            </h3>
            <ul className="space-y-2 font-pangram text-[14px] leading-[22px] text-white/80">
              <li>
                <button type="button" onClick={() => openLegal('terms')} className="text-left hover:text-white">
                  Terms of Use
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openLegal('privacy')} className="text-left hover:text-white">
                  Privacy &amp; Cookies Policy
                </button>
              </li>
              <li>
                <button type="button" onClick={() => openLegal('user-safety-code')} className="text-left hover:text-white">
                  User Safety Codex
                </button>
              </li>
            </ul>
          </div>

          <div className="col-span-1 space-y-6 justify-self-start sm:space-y-8 lg:col-start-4 lg:justify-self-start">
            <div>
              <h3 className="mb-3 font-pangram text-[14px] font-semibold leading-[20px] tracking-[0.02em]">
                {t.footer_follow || 'Follow Us'}
              </h3>
              <div className="flex items-center gap-3 text-white sm:gap-4">
                <a
                  href="https://www.facebook.com/profile.php?id=61574953304741"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors duration-200 hover:text-[#6195FF]"
                >
                  <FacebookIcon />
                </a>
                <a
                  href="https://www.tiktok.com/@lucidbookapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors duration-200 hover:text-[#6195FF]"
                >
                  <TikTokIcon />
                </a>
                <a
                  href="https://www.instagram.com/lucidbookapp/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors duration-200 hover:text-[#6195FF]"
                >
                  <InstagramIcon />
                </a>
                <a
                  href="https://discord.gg/7T26CGkbB2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors duration-200 hover:text-[#6195FF]"
                >
                  <DiscordIcon />
                </a>
                <a
                  href="https://x.com/lucidbook?s=21"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors duration-200 hover:text-[#6195FF]"
                >
                  <XIcon />
                </a>
              </div>
            </div>

            <div>
              <h3 className="mb-2 font-pangram text-[14px] font-semibold leading-[20px] tracking-[0.02em]">
                {t.footer_contact || 'Contact Us'}
              </h3>
              <a
                href="mailto:support@lucidbook.com"
                className="inline-flex items-center gap-1.5 font-pangram text-[11px] leading-[16px] text-white/80 hover:text-white sm:gap-2 sm:text-[12px] sm:leading-[22px]"
              >
                <svg width="24" height="24" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path
                    d="M3.333 5.833h13.334v8.334H3.333V5.833Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3.75 6.25 10 10.625 16.25 6.25"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                </svg>
                support@lucidbook.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
